import { useEffect, useRef } from 'react'
import type { Action } from '../../domain/action'
import { actionStatsLabel, rewardRateLabel } from '../../domain/action'
import type { Area } from '../../domain/area'
import { CheckCircleIcon, CloseIcon, CounterIcon, PlayIcon, TimerIcon, TrophyIcon } from './Icons'
import { useI18n } from '../../i18n'

interface ActionPreviewSheetProps {
  action: Action | null
  area: Area | null
  hasActiveAction: boolean
  open: boolean
  saving: boolean
  specializationLabel: string
  onClose: () => void
  onAnother?: () => void
  onActivate: () => Promise<void>
}

const ActionIcon = ({ type }: { type: Action['type'] }) => type === 'timer' ? <TimerIcon /> : type === 'counter' ? <CounterIcon /> : <CheckCircleIcon />

export const ActionPreviewSheet = ({ action, area, hasActiveAction, open, saving, specializationLabel, onClose, onAnother, onActivate }: ActionPreviewSheetProps) => {
  const { language, copy: { common, preview } } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  if (!action || !area) return null

  return (
    <dialog ref={dialogRef} className="sheet preview-sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <div className="sheet__body">
        <div className="sheet__handle" />
        <div className="preview-heading">
          <span className="preview-icon" style={{ color: area.color, backgroundColor: `${area.color}29` }}><ActionIcon type={action.type} /></span>
          <div><h2>{action.name}</h2><p>{area.name} · {specializationLabel || common.noSkill}</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={common.close}><CloseIcon /></button>
        </div>
        <p className="preview-stats" style={{ color: area.color }}>{actionStatsLabel(action, language)}</p>
        <p className="preview-reward">{rewardRateLabel(action)}</p>
        {action.repeatMode === 'once' && <p className="preview-unique"><TrophyIcon /> {preview.unique}</p>}
        <p className="preview-help">{preview.help}</p>
        {hasActiveAction && <p className="replace-note">{preview.replaceHelp}</p>}
        <div className="preview-actions">
          {onAnother && <button className="button button--ghost" type="button" onClick={onAnother}>{preview.another}</button>}
          <button className="button button--complete" type="button" disabled={saving} onClick={() => void onActivate()}><PlayIcon />{hasActiveAction ? preview.replace : preview.activate}</button>
        </div>
      </div>
    </dialog>
  )
}
